<script setup>

import { onMounted, ref, watch } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import transactionService from '@/services/transaction.service'
import amountTransactionService from '@/services/amount_transaction.service'
import batchService from '@/services/batch.service'
import taskService from '@/services/task.service'
import enumService from '@/services/enum.service'
import ProgressSpinner from 'primevue/progressspinner'
import TaskSaveModal from '@/components/modal/TaskSaveModal.vue'
import TaskDeleteModal from '@/components/modal/TaskDeleteModal.vue'
import Icon from '@/components/Icon/Icon.vue'
import { FilterMatchMode } from 'primevue/api'
import moment from 'moment'
import 'moment/dist/locale/fr'
import { useRoute } from 'vue-router'
import currencyService from '@/services/currency.service'
import TaskInventory from '@/components/task/TaskInventory.vue'
import TaskReferenceIndex from '@/components/task/TaskReferenceIndex.vue'

const currentYear = ref(moment().format('YYYY'))
const transactions = ref([])
const loadedComponent = ref(false)
const rentings = ref([])
const selectedRenting = ref({})
const rentingFilter = ref({})
const currencies = ref([])
const taskSaveModal = ref(null)
const taskDeleteModal = ref(null)
const year = ref(currentYear.value)
const expandedRows = ref([])
const dt = ref()
const route = useRoute()
const tasks = ref([])
const taskStatus = ref([])
const taskType = ref([])
const range = ref(false)
const reloads = ref({})

const props = defineProps({
    renting: {
        type: Object,
        required: false,
        default: {}
    },
    limit: {
        type: Number,
        required: false,
        default: 0
    },
})

watch(() => props.renting, (rentingVal) => {
    loadedComponent.value = false

    rentingFilter.value = rentingVal

    transactionService.get({'params': {
            sort: "date",
            desc: "date",
            renting_uuid: rentingFilter.value.uuid,
            'date:gte': year.value + '-01-01',
            'date:lte': year.value + '-12-31',
        }}).then(data => {
            transactions.value = data

            loadedComponent.value = true
    })
}, { deep: 1 })

const newTransaction = () => {
    taskSaveModal.value.open({renting_uuid: selectedRenting.value.uuid})
}

const editTask = (taskVal) => {
    taskSaveModal.value.open({...taskVal})
}

const confirmDeleteTask = (taskVal) => {
    taskDeleteModal.value.open({...taskVal})
}

const addTask = (taskVal) => {
    taskVal.amount_used = 0.0

    tasks.value.push(Object.assign({}, taskVal))
}

const updateTask = (taskVal) => {
    tasks.value.find((o, i) => {
        if(o.uuid === taskVal.uuid) {
            tasks.value[i] = JSON.parse(JSON.stringify(taskVal))

            reloads.value[tasks.value[i].uuid] = reloads.value[tasks.value[i].uuid] + 1

            return true
        }
    })
}

const deleteTask = (taskVal) => {
    tasks.value.find((o, i) => {
        if(o.uuid === taskVal.uuid) {
            tasks.value.splice(i, 1)

            return true
        }
    })
}

const exportCSV = () => {
    dt.value.exportCSV()
}

const showPaid = (data) => {
    if(data.amount_used == data.amount) {
        return 'bg-lime-300'
    } else if(data.amount_used > 0) {
        return 'bg-amber-200'
    }

    return 'bg-red-300'
}

const getCurrencySymbol = (currencyId) => {
    const found = currencies.value.find((o, i) => {
        if(o.id === currencyId) {
            return true
        }
    })

    if(found) {
        return found.symbol
    }

    return ''
}

const showStatus = (status) => {
    return taskStatus.value[status].name
}

const showType = (type) => {
    let typeString = ''

    switch (type) {
        case "inventory":
            typeString = "État des lieux"
            break

        case "reference_index":
            typeString = "Index de référence"
            break
    }

    return typeString
}

const showStatusColor = (status) => {
    if(status == 0) {
        return 'bg-amber-200'
    }
    else if(status == 1) {
        return 'bg-lime-300'
    }

    return 'bg-gray-300'
}

const showDate = (date) => {
    const d = moment(date)

    if(d.isValid()) {
        return d.format('DD MMM YYYY')
    }

    return ''
}

const loadTaskComponent = (type) => {
    if(type === 'inventory') {
        return TaskInventory
    } else if(type === 'reference_index') {
        return TaskReferenceIndex
    }

    return ''
}

const changeTask = (taskUuid) => {
    tasks.value.find((o, i) => {
        if(o.uuid === taskUuid) {
            reloads.value[tasks.value[i].uuid] = reloads.value[tasks.value[i].uuid] + 1

            return true
        }
    })
}

onMounted(() => {
    enumService.get('task-status')
    .then(response => {
        taskStatus.value = response.data.task_status
    })

    enumService.get('task-type')
    .then(response => {
        taskType.value = response.data.task_type
    })

    let params = {}

    if(props.limit > 0) {
        params = {params: {
            sort: 'date',
            desc: 'date',
            range: '0-' + props.limit.toString()
        }}
    } else {
        params = {params: {
            sort: 'date',
            desc: 'date',
        }}
    }
    taskService.get(params)
    .then((response) => {
        tasks.value = response.data.tasks

        tasks.value.forEach((item) => {
            reloads.value[item.uuid] = 0
        })

        loadedComponent.value = true
    })

    currencyService.get({'params': {fields: 'id,name,symbol'}})
    .then(response => {
        currencies.value = response
    })
})

</script>

<template>
    <TaskSaveModal
        ref="taskSaveModal"
        :externRentings="rentings"
        @addTask="addTask"
        @updateTask="updateTask"
    />
    <TaskDeleteModal
        ref="taskDeleteModal"
        @deleteTask="deleteTask"
    />

    <div v-if="loadedComponent" class="mx-4">
        <div v-if="limit == 0" class="mt-8 w-full flex justify-between gap-4">
            <Button type="button" @click="newTransaction()" class="px-4 py-2 flex justify-center gap-2 shadow-md border border-black bg-green-400 rounded hover:shadow-none hover:bg-green-500">
                <Icon name="add" :size="12" color="black" class="w-4 h-4" /> Nouveau
            </Button>
            <Button type="button" @click="exportCSV($event)" class="px-4 py-2 flex justify-center gap-2 shadow-md border border-black bg-gray-100 rounded hover:shadow-none hover:bg-gray-300">
                <Icon name="export" :size="12" color="black" class="w-4 h-4" /> Exporter
            </Button>
        </div>
        <div class="mt-8">
            <DataTable
                :value="tasks"
                size="small"
                removableSort
                :globalFilterFields="['type', 'name', 'date', 'status']"
                filterDisplay="row"
                tableStyle="min-width: 40rem"
                v-model:expandedRows="expandedRows"
                paginator
                :rows="25"
                :rowsPerPageOptions="[10, 25, 50]"
            >
                <template #empty>
                    <span class="flex justify-center w-full p-2">
                        Aucune location
                    </span>
                </template>

                <Column expander style="width: 3rem" />
                <Column field="type" header="Type" sortable>
                    <template #body="{ data }">
                        {{ showType(data.type) }}
                    </template>
                </Column>
                <Column field="name" header="Nom" sortable />
                <Column field="date" header="Date" sortable>
                    <template #body="{ data }">
                        {{ showDate(data.date) }}
                    </template>
                </Column>
                <Column field="status" header="Statut" sortable>
                    <template #body="{ data }">
                        <span class="rounded p-1" :class="showStatusColor(data.status)">{{ showStatus(data.status) }}</span>
                    </template>
                </Column>
                <Column :exportable="false" style="width: 4rem">
                    <template #body="slotProps">
                        <div class="flex gap-1">
                            <button class="rounded border-2 border-black cursor-pointer p-2 shadow-md hover:shadow-none hover:bg-green-500" @click="editTask(slotProps.data)" title="Modifier la tâche">
                                <Icon name="edit" :size="12" color="black" class="w-5 h-5" />
                            </button>
                            <button class="rounded border-2 border-black cursor-pointer p-2 shadow-md hover:shadow-none hover:bg-red-500" @click="confirmDeleteTask(slotProps.data)" title="Supprimer la tâche">
                                <Icon name="delete" :size="12" color="black" class="w-5 h-5" />
                            </button>
                        </div>
                    </template>
                </Column>

                <template #expansion="slotProps">
                    <div :key="reloads[slotProps.data.uuid]">
                        <div v-if="slotProps.data.comment" class="px-10 py-2">
                            {{ slotProps.data.comment }}
                        </div>
                        <div class="px-10 py-2">
                            <component :is="loadTaskComponent(slotProps.data.type)" :data="slotProps.data" @changeTask="changeTask" />
                        </div>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
    <div v-else class="mx-4 h-[300px] w-full flex justify-center">
        <ProgressSpinner
            style="width: 50px; height: 50px"
            strokeWidth="5"
            fill="transparent"
            animationDuration="2.5s"
            aria-label="Custom ProgressSpinner"
            class="m-auto"
        />
    </div>
</template>
